import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, RefreshCw, Loader2, ArrowUpRight, Wallet, CreditCard, Banknote,
} from "lucide-react";

const STATUS_COLOR = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  initiated: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  refund: "bg-violet-50 text-violet-700 border-violet-200",
};

const fmtIST = (iso) => {
  if (!iso) return "—";
  try { const d = new Date(iso); return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return "—"; }
};

export default function AdminOutboundPayments() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [gateway, setGateway] = useState("all");
  const [status, setStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (gateway !== "all") params.gateway = gateway;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/admin/payment-links/outbound-payments", { params });
      setRows(data.items || []);
      setTotals(data.totals || {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load outbound payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPaid = useMemo(
    () => rows.filter((r) => r.status === "paid").reduce((acc, r) => acc + (parseFloat(r.amount) || 0), 0),
    [rows]
  );

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="outbound-payments-page">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Outbound Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Unified ledger of every PG transaction across providers. One row per customer Pay attempt.
          </p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Banknote} label="Sessions (filtered)" value={rows.length} />
          <Kpi icon={Wallet} label="Paid amount" value={`₹${totalPaid.toFixed(2)}`} />
          <Kpi icon={CreditCard} label="Paid sessions" value={totals.paid || 0} />
          <Kpi icon={ArrowUpRight} label="Failed / pending" value={(totals.failed || 0) + (totals.pending || 0) + (totals.initiated || 0)} />
        </div>

        {/* Filters */}
        <div className="surface-card p-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 flex-1 min-w-64">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by UTR / provider ref / txnid / mobile / bank / payer name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              className="h-9"
              data-testid="outbound-search"
            />
          </div>
          <Select value={gateway} onValueChange={setGateway}>
            <SelectTrigger className="h-9 w-44" data-testid="filter-gateway"><SelectValue placeholder="Gateway" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All gateways</SelectItem>
              <SelectItem value="manarecharge">ManaRecharge</SelectItem>
              <SelectItem value="razorpay">Razorpay</SelectItem>
              <SelectItem value="cashfree">Cashfree</SelectItem>
              <SelectItem value="runpaisa">Runpaisa</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="initiated">Initiated</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={load} disabled={loading} data-testid="outbound-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Apply</span>
          </Button>
        </div>

        {/* Table */}
        <div className="surface-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Provider Ref / UTR</th>
                  <th className="text-left px-3 py-2 font-medium">Provider Order ID</th>
                  <th className="text-left px-3 py-2 font-medium">Gateway · Channel</th>
                  <th className="text-left px-3 py-2 font-medium">Paymode</th>
                  <th className="text-left px-3 py-2 font-medium">Bank · Card</th>
                  <th className="text-left px-3 py-2 font-medium">Payer</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Link</th>
                </tr>
              </thead>
              <tbody data-testid="outbound-tbody">
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={10} className="px-3 py-10 text-center text-sm text-muted-foreground">No outbound payments match your filter.</td></tr>
                )}
                {rows.map((r, i) => {
                  const sc = STATUS_COLOR[r.status] || STATUS_COLOR.pending;
                  return (
                    <tr key={`${r.link_id}-${i}`} className="border-b hover:bg-accent/30" data-testid={`outbound-row-${i}`}>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtIST(r.created_at)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.provider_ref || r.utr || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.provider_order_id || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="capitalize">{r.gateway || "—"}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{r.pg_channel || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-mono">{r.mode_pg || "—"}</div>
                        {r.method_type && <div className="text-[10px] text-muted-foreground">{r.method_type}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{r.bank_name || "—"}</div>
                        {r.card_last4 && (
                          <div className="text-[10px] text-muted-foreground font-mono">
                            **** {r.card_last4}{r.card_network ? ` · ${r.card_network}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{r.payer_name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{r.mobile || "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">₹{Number(r.amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${sc}`}>{r.status || "—"}</Badge></td>
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-[10px] font-mono"
                          onClick={() => navigate(`/admin/payment-links/${r.link_id}`)}
                          data-testid={`outbound-open-link-${i}`}
                          title={r.link_id}
                        >
                          {(r.link_id || "").slice(0, 8)}…
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="font-semibold text-lg">{value}</div>
    </div>
  );
}
